import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { authCheck } from '../../store/auth/actions'
import { Tooltip } from 'react-tooltip'

import styles from './styles.module.scss'
import { getPlaces, getCompanyIntegrations } from '../../api'
import { useQuery } from '../../hooks/useQuery'

import usePermissions from '../../components/Core/usePermissions'
import MainLayout from '../../components/Core/MainLayout'
import AppNavbar from '../../components/Core/AppNavbar'
import PlacesMap from '../../components/Places/Map'
import Overview from '../../components/Places/Overview'
import PlaceRow from '../../components/Places/PlaceRow'
import SortButton from '../../components/Places/SortButton'
import Documents from '../../components/Places/Documents'
import Expenses from '../../components/Places/Expenses'
import Attendance from '../../components/Places/Attendance'
import Profitability from '../../components/Places/Profitability'
import ShiftProfitability from '../../components/Places/ShiftProfitability'

const getCenter = (coords) => {
  const total = coords.length;

  const sum = coords.reduce((acc, { lat, lng }) => {
      acc.lat += lat;
      acc.lng += lng;
      return acc;
    },
    { lat: 0, lng: 0 }
  );

  return {
    lat: sum.lat / total,
    lng: sum.lng / total,
  };
}

const reverseNumber = (num) => {
  let value = Math.abs(num);
  let reversed = 0;

  while (value > 0) {
    reversed = reversed * 10 + (value % 10);
    value = Math.floor(value / 10);
  }

  return num < 0 ? -reversed : reversed;
}

const getColorFromId = (id) => {
  id = `${reverseNumber(id)}${id}`
  let hash = 0;

  const str = id.toString();

  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += value.toString(16).padStart(2, '0');
  }

  return color;
}

const PlacesScreen = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { id: companyId } = useParams()
  const permissions = usePermissions([{
    name: 'places_use_documents',
    module: 'places_use_documents',
  }, {
    name: 'can_manage_documents',
    permission: 'places_can_manage_documents',
  }, {
    name: 'use_expenses',
    module: 'use_expenses',
  }, {
    name: 'can_view_expenses',
    permission: 'places_can_view_expenses'
  }, {
    name: 'can_manage_expenses',
    permission: 'places_can_manage_expenses'
  }, {
    name: 'can_see_attendance',
    permission: 'places_can_see_attendance'
  }, {
    name: 'can_see_profitability',
    permission: 'places_can_see_profitability',
    module: 'places_profitability',
  }, {
    name: 'schedule_shift',
    module: 'schedule_shift',
  }, {
    name: 'iiko_module',
    module: 'iiko_profitability',
    permission: 'places_see_iiko_profitability',

  }])

  const history = useHistory()
  const { tab: activeTab } = useQuery(['tab:map'])
  const employee = useSelector(state => state.auth?.user?.employee || {})

  const [places, setPlaces] = useState([])
  const [activePlace, setActivePlace] = useState(null)
  const [sort, setSort] = useState({type: 'clockin', order: 'desc'})
  const [highlightedPlaceId, setHighlightedPlaceId] = useState(null)
  const [query, setQuery] = useState('')
  const [integrations, setIntegrations] = useState({})

  const center = getCenter(places.filter(p => p.coordinates).map(p => ({ lat: p.lat, lng: p.lng })))
  const [assignedPlace] = employee.place || []

  const groupedPlaces = useMemo(() => {
    const sortedPlaces = [...places].filter(p => !assignedPlace || assignedPlace.id === p.id).filter(p => p.name.toLowerCase().includes(query.toLowerCase())).sort((a, b) => {
      if (sort.type === 'name') {
        if (sort.order === 'asc') {
          return a.name.localeCompare(b.name)
        }
        return b.name.localeCompare(a.name)
      }
      if (sort.type === 'clockin') {
        if (sort.order === 'asc') {
          return a.active_timer_count - b.active_timer_count
        }
        return b.active_timer_count - a.active_timer_count
      }
      return 0
    })
    return sortedPlaces.reduce((acc, place) => {
      if (!place.coordinates) {
        return {
          ...acc,
          other: [...(acc.other || []), place]
        }
      }
      return {
        ...acc,
        [place.type]: [...(acc[place.type] || []), place]
      }
    }, {place: [], project: [], other: []})
  }, [places, sort, query, assignedPlace])

  const options = [{
    title: t('Map'),
    key: 'map',
    badge: 0,
  },
  ...(permissions.can_see_attendance ? [{
    title: t('Attendance'),
    key: 'attendance',
    badge: 0,
  }] : []),
  ...(permissions.places_use_documents ? [{
    title: t('Documents'),
    key: 'documents',
    badge: 0,
  }] : []),
  ...(permissions.use_expenses && permissions.can_view_expenses ? [{
    title: t('Expenses'),
    key: 'expenses',
    badge: 0,
  }] : []),
  ...(permissions.can_see_profitability ? [{
    title: t('Profitability'),
    key: 'profitability',
    badge: 0,
  }] : []),
  ...(permissions.iiko_module && integrations.use_iiko_profitability ? [{
    title: t('Shift profitability'),
    key: 'iiko_profitability',
    badge: 0,
  }] : [])
]

  useEffect(() => {
    dispatch(authCheck())
    getPlaces(companyId, {active_timer: true}).then(res => {
      if (Array.isArray(res)) {
        const filteredPlaces = res.map(p => {
          if (!p.coordinates) {
            return p
          }
          const [plat, plng] = p.coordinates.split(',')
          const [latNum, latDec] = plat.split('.')
          const [lngNum, lngDec] = plng.split('.')
          const lat = `${latNum}.${latDec.slice(0, 7)}`
          const lng = `${lngNum}.${lngDec.slice(0, 7)}`
          return { ...p, lat: parseFloat(lat), lng: parseFloat(lng) }
        }).map(p => ({ ...p, color: getColorFromId(p.id) }))
        setPlaces(filteredPlaces)
      }
    })
    getCompanyIntegrations(companyId).then(res => {
      if (res) {
        setIntegrations(res)
      }
    })
  }, [companyId])

  const handlePressPlace = (place) => () => {
    setActivePlace(prev => prev?.id === place.id ? null : place)
  }

  const handleSelectTab = key => {
    history.push({search: `?tab=${key}`})
  }

  const handleSortChange = (type) => () => {
    setSort(prev => {
      if (prev.type === type) {
        return {
          type,
          order: prev.order === 'asc' ? 'desc' : 'asc'
        }
      }
      return {
        type,
        order: 'asc'
      }
    })
  }

  const handleEnterPlace = (placeId) => {
    setHighlightedPlaceId(placeId)
  }

  const handleLeavePlace = (placeId) => {
    setHighlightedPlaceId(prev => prev === placeId ? null : prev)
  }

  const handleUpdatedDescription = (placeId, description) => {
    setPlaces(prev => prev.map(p => p.id === placeId ? {...p, folder_description: description} : p))
    setActivePlace(prev => prev?.id === placeId ? {...prev, folder_description: description} : prev)
  }

  return (
    <MainLayout layoutClassName={styles.layout} screenClassName={styles.screen}>
      <div className={styles.placesScreen}>
        <div className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <div className={styles.sidebarHeaderContent}>
              <div style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}} onClick={handleSortChange('name')}>
                <div className={styles.sidebarTitle}>{t('Place name')}</div>
                <SortButton sortType={sort.type === 'name' && sort.order} />
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}} onClick={handleSortChange('clockin')}>
                <div className={styles.sidebarTitle}>{t('People clocked in')}</div>
                <SortButton sortType={sort.type === 'clockin' && sort.order} />
              </div>
            </div>
          </div>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder={t('Search by name')}
              className={styles.searchInput}
              value={query}
              onChange={({target}) => setQuery(target.value)}  />
          </div>
          <div className={styles.sidebarContent}>
            {
              groupedPlaces.project.length
                ? <>
                    <div className={styles.groupTitle}>{t('Projects')}</div>
                    {
                      groupedPlaces.project.map(place => {
                        return (
                          <PlaceRow
                            key={place.id}
                            place={place}
                            isActive={activePlace?.id === place.id}
                            onPress={handlePressPlace}
                            onEnter={handleEnterPlace}
                            onLeave={handleLeavePlace} />
                        )
                      })
                    }
                  </>
                : null
            }
            {
              groupedPlaces.place.length
                ? <>
                    <div className={styles.groupTitle}>{t('Places')}</div>
                    {
                      groupedPlaces.place.map(place => {
                        return (
                          <PlaceRow
                            key={place.id}
                            place={place}
                            isActive={activePlace?.id === place.id}
                            onPress={handlePressPlace}
                            onEnter={handleEnterPlace}
                            onLeave={handleLeavePlace} />
                        )
                      })
                    }
                  </>
                : null
            }
            {
              groupedPlaces.other.length
                ? <>
                    <div className={styles.groupTitle}>{t('Other')}</div>
                    {
                      groupedPlaces.other.map(place => {
                        return (
                          <PlaceRow
                            key={place.id}
                            place={place}
                            isActive={activePlace?.id === place.id}
                            onPress={handlePressPlace}
                            onEnter={handleEnterPlace}
                            onLeave={handleLeavePlace} />
                        )
                      })
                    }
                  </>
                : null
            }
          </div>
        </div>
        <div className={styles.content}>
          <AppNavbar
            options={options}
            selected={activeTab}
            className={styles.navbar}
            highlighted={false}
            onSelect={handleSelectTab}
          />
          {
            ((tab) => {
              switch (tab) {
                case 'map':
                  return (
                    <PlacesMap
                      highlightedPlaceId={highlightedPlaceId}
                      selectedPlace={activePlace?.lat && activePlace?.lng && activePlace}
                      lat={activePlace?.lat || center.lat || 0}
                      lng={activePlace?.lng || center.lng || 0}
                      places={places.filter(p => p.coordinates)}
                      onChangePlace={setActivePlace} />
                  )
                case 'attendance':
                  return (
                    <Attendance
                      placeId={activePlace?.id}
                      selectedPlace={activePlace}
                      companyId={companyId} />
                  )
                case 'overview':
                  return <Overview selectedPlace={activePlace} />
                case 'documents':
                  return (
                    <Documents
                      placeId={activePlace?.id}
                      selectedPlace={activePlace}
                      companyId={companyId}
                      canManage={permissions.can_manage_documents}
                      onUpdatedDescription={handleUpdatedDescription} />
                  )
                case 'expenses':
                  return (
                    <Expenses
                      companyId={companyId}
                      placeId={activePlace?.id}
                      selectedPlace={activePlace} />
                  )
                case 'profitability':
                  return (
                    <Profitability
                      companyId={companyId}
                      placeId={activePlace?.id}
                      selectedPlace={activePlace} />
                  )
                case 'iiko_profitability':
                  return (
                    <ShiftProfitability
                      companyId={companyId}
                      placeId={activePlace?.id}
                      selectedPlace={activePlace} />
                  )
                default:
                  return null
              }
            })(activeTab)
          }
        </div>
      </div>
      <Tooltip
        id="address"
        variant="light"
        place="top-start"
        opacity={1} />
    </MainLayout>
  )
}

export default PlacesScreen
